import queryString from 'query-string';

import { initialState } from '@/Helpers.js';
import Model from '@/models/Model.js';
import User from '@/models/User.js';
import WidgetTemplate from '@/models/WidgetTemplate.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default class Widget extends Model {
    static entity = 'widgets';

    static modelName = 'Widget';

    static fields() {
        return {
            id: this.attr(null),
            widget_template_id: this.attr(null),
            user_id: this.attr(null),
            name: this.attr(null),
            type: this.attr(null),
            description: this.attr(null),
            color: this.attr(null),
            settings: this.attr(null),
            layout_x: this.attr(null),
            layout_y: this.attr(null),
            layout_width: this.attr(null),
            layout_height: this.attr(null),

            ...super.fields(),

            widget_template: this.belongsTo(WidgetTemplate, 'widget_template_id'),
            user: this.belongsTo(User, 'user_id'),

            is_preview: this.attr(false),
        };
    }

    /**
     * @returns {{w: (number|*), x: (number|*), h: (number|*), name: *, y: (*|number), i}}
     */
    get layout() {
        /* eslint-disable id-denylist */
        return {
            x: this.layout_x,
            y: this.layout_y,
            w: this.layout_width,
            h: this.layout_height,
            i: this.id,
            name: this.type_name,
        };
        /* eslint-enable id-denylist */
    }

    /**
     * @returns {*|null}
     */
    get background() {
        return this.color ? this.color : null;
    }

    /**
     * @returns {*}
     */
    get type_name() {
        return initialState('WidgetTypes')[this.type];
    }

    /**
     * @returns {string}
     */
    get signature() {
        return `${this.type}-${JSON.stringify(this.settings?.filters)}`;
    }

    /**
     * Returns an array of every cell that this widget occupies on the grid according to its coordinates and dimensions.
     * Each item in the array is an array of the column and row of the cell ([x, y]). Basically a cell coordinate.
     * @returns {*[]}
     */
    get occupied_grid_cells() {
        const cells = [];

        for (let column = this.layout_x; column < this.layout_x + this.layout_width; column++) {
            for (let row = this.layout_y; row < this.layout_y + this.layout_height; row++) {
                cells.push([column, row]);
            }
        }

        return cells;
    }

    /**
     * @returns {number}
     */
    get per_page() {
        return this.layout_height <= 8 ? 5 : 15;
    }

    /**
     * @param isMobile
     * @returns {string}
     */
    displayMode(isMobile) {
        if (isMobile || this.layout_width <= 4) return 'small';

        if (this.layout_width < 12) return 'medium';

        if (this.layout_width === 12) return 'large';

        return 'small';
    }

    /**
     * @param params
     * @returns {Promise<{}|any>}
     */
    async hydrate(params = {}) {
        if (!this.id) return {};

        try {
            const { data } = await axios.get(
                `/api/widgets/${this.id}?preload=true&per_page=${this.per_page}&${queryString.stringify(params)}`,
                {
                    errorHandle: false,
                }
            );

            return data;
        } catch (_error) {
            throw new Error('There was an error retrieving data for this widget.');
        }
    }

    /**
     * @param filters
     * @returns {Promise<*>}
     */
    async filter(filters) {
        const settings = { ...this.settings, filters: { ...(this.settings.filters || {}), ...filters } };

        await this.$update(settings);

        try {
            const { response } = await Widget.api().put(
                `/api/widgets/${this.id}?reload=true&per_page=${this.per_page}`,
                { settings },
                { errorHandle: false }
            );

            return response.data;
        } catch (_error) {
            throw new Error('There was an error filtering this widget.');
        }
    }

    /**
     * @param layout
     * @returns {*&{layout_width: *, layout_height: *, layout_x: *, layout_y: *}}
     */
    layoutPayload(layout) {
        return {
            ...this.$toJson(),
            ...{
                layout_x: layout.x,
                layout_y: layout.y,
                layout_width: layout.w,
                layout_height: layout.h,
            },
        };
    }

    /**
     * @param filters
     * @param queryModifier
     * @returns {Promise<*>}
     */
    async changeFilters(filters, queryModifier) {
        const existingSettings = this.settings || {};

        const payload = {
            settings: {
                ...existingSettings,
                filters,
            },
        };

        await this.$update(payload);

        const query = new QueryBuilder(`/api/widgets/${this.id}`);

        if (queryModifier) {
            queryModifier(query);
        }

        const { response } = await Widget.api().put(query.toUrl(), payload);

        return response.data;
    }

    /**
     * @param queryModifier
     * @returns {Promise<*>}
     */
    async hydrateSidebar(queryModifier) {
        const query = new QueryBuilder(`/api/widgets/${this.id}`);

        if (queryModifier) {
            queryModifier(query);
        }

        const { response } = await Widget.api().get(query.toUrl());

        return response.data;
    }

    /**
     * @param dimensions
     * @returns {*}
     */
    async changeSize(dimensions) {
        try {
            return await Widget.api().put(`/api/widgets/${this.id}`, { ...this.$toJson(), ...dimensions });
        } catch (_error) {
            throw new Error('There was an error updating this widget.');
        }
    }

    /**
     * @param name
     * @returns {*}
     */
    changeName(name) {
        const payload = this.$toJson();

        payload.name = name;

        return Widget.api().put(`/api/widgets/${this.id}`, payload);
    }

    /**
     * @returns {*}
     */
    async remove() {
        try {
            return await Widget.api().delete(`/api/widgets/${this.id}`);
        } catch (_error) {
            throw new Error('There was an error removing this widget.');
        }
    }
}
